import { useEffect } from 'react';
import {
  APPEARANCE_APPLIED_EVENT,
  type AppliedAppearance,
  type AppSettings,
  applyAppSettings,
  readAppSettings,
  resolveDesktopTheme,
  SETTINGS_CHANGED_EVENT,
} from '@/lib/app-settings';
import type { DesktopShellStore } from '@/lib/desktop-shell/store';

/**
 * Loads persisted app settings on mount, applies them (including the
 * `data-theme` on `document.body`, restored on unmount), and — while the theme
 * is `system` — re-applies on OS color-scheme changes. It follows the theme and
 * density the settings page applies, and the settings it saves.
 */
export function useDesktopTheme({
  store,
  appSettings,
}: {
  store: DesktopShellStore;
  appSettings: AppSettings;
}): void {
  useEffect(() => {
    const settings = readAppSettings();
    store.getState().setAppSettings(settings);
    applyAppSettings(settings);
    const previousTheme = document.body.dataset['theme'];
    document.body.dataset['theme'] = resolveDesktopTheme(settings.theme);
    return () => {
      if (previousTheme === undefined) delete document.body.dataset['theme'];
      else document.body.dataset['theme'] = previousTheme;
    };
  }, [store]);

  // The settings page previews the theme, appearance and density before saving, and saves the rest: the
  // shell (and the sidebar it hosts for that page) follows both.
  useEffect(() => {
    const followAppearance = (event: Event) => {
      const { theme, compactMode, appearance } = (event as CustomEvent<AppliedAppearance>).detail;
      store
        .getState()
        .setAppSettings((current) =>
          current.theme === theme &&
          current.compactMode === compactMode &&
          current.appearance === appearance
            ? current
            : { ...current, theme, compactMode, appearance },
        );
    };
    const followSaved = () => store.getState().setAppSettings(readAppSettings());
    window.addEventListener(APPEARANCE_APPLIED_EVENT, followAppearance);
    window.addEventListener(SETTINGS_CHANGED_EVENT, followSaved);
    return () => {
      window.removeEventListener(APPEARANCE_APPLIED_EVENT, followAppearance);
      window.removeEventListener(SETTINGS_CHANGED_EVENT, followSaved);
    };
  }, [store]);

  useEffect(() => {
    if (appSettings.theme !== 'system') return;
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const syncSystemTheme = () => {
      applyAppSettings(appSettings);
      store.getState().setAppSettings((current) => ({ ...current }));
    };
    media.addEventListener('change', syncSystemTheme);
    return () => media.removeEventListener('change', syncSystemTheme);
  }, [store, appSettings]);
}
